import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export type NavigationItem = {
  path?: string;
  key: string;
  value?: string;
  name?: string;
  items?: NavigationItem[];
  type?: string;
};

export type LmsConfigState = {
  workflowActionState: 'initial' | 'loading' | 'success' | 'error' | null;
  workflowActionMessage: string | null;
  hierarchy: {
    user?: any;
    revyreHierarchy?: any;
  } | null;
  inputFields: Record<string, any>;
  selectedMenu: { menu: NavigationItem | null; type: 'direct' | 'parent' };
  users: any;
  allUsers: any;
  referrals: any;
  revyreConfig: any;
  pendingActivities: any;
  performanceCards: any;
};

const initialState: LmsConfigState = {
  workflowActionState: null,
  workflowActionMessage: null,
  hierarchy: {
    user: {},
    revyreHierarchy: {},
  },
  users: {},
  allUsers: {},
  referrals: [],
  revyreConfig: {},
  inputFields: {},
  selectedMenu: { menu: null, type: 'direct' },
  pendingActivities: [],
  performanceCards: [],
};

export const lmsConfigSlice = createSlice({
  name: 'lmsConfig',
  initialState,
  reducers: {
    setWorkflowActionState: (
      state,
      action: PayloadAction<LmsConfigState['workflowActionState']>,
    ) => {
      state.workflowActionState = action.payload;
      if (action.payload === 'initial') {
        state.workflowActionMessage = null;
      }
    },
    setWorkflowActionMessage: (
      state,
      action: PayloadAction<LmsConfigState['workflowActionMessage']>,
    ) => {
      if (action.payload) {
        state.workflowActionMessage = action.payload;
      }
    },
    setHierarchy: (
      state,
      action: PayloadAction<LmsConfigState['hierarchy']>,
    ) => {
      state.hierarchy = { ...state.hierarchy, ...action.payload };
    },
    setInputFields: (
      state,
      action: PayloadAction<LmsConfigState['inputFields']>,
    ) => {
      state.inputFields = action.payload;
    },
    setSelectedMenu: (
      state,
      action: PayloadAction<LmsConfigState['selectedMenu']>,
    ) => {
      state.selectedMenu = action.payload;
    },
    setAllUsers: (state, action: PayloadAction<LmsConfigState['allUsers']>) => {
      state.allUsers = action.payload;
    },
    setUsers: (state, action: PayloadAction<LmsConfigState['users']>) => {
      state.users = action.payload;
    },
    setReferrals: (
      state,
      action: PayloadAction<LmsConfigState['referrals']>,
    ) => {
      state.referrals = action.payload;
    },
    setRevyreConfig: (
      state,
      action: PayloadAction<LmsConfigState['revyreConfig']>,
    ) => {
      state.revyreConfig = action.payload;
    },
    setPerformanceCards: (
      state,
      action: PayloadAction<LmsConfigState['performanceCards']>,
    ) => {
      state.performanceCards = action.payload;
    },
    resetWorkflowAction: (state) => {
      state.workflowActionMessage = null;
      state.workflowActionState = null;
    },
    setPendingActivities: (
      state,
      action: PayloadAction<LmsConfigState['pendingActivities']>,
    ) => {
      state.pendingActivities = action.payload;
    },
    removeFirstPendingActivity: (state) => {
      state.pendingActivities.shift();
    },
  },
});

export const {
  setWorkflowActionState,
  setWorkflowActionMessage,
  setInputFields,
  setSelectedMenu,
  resetWorkflowAction,
  setHierarchy,
  setAllUsers,
  setUsers,
  setReferrals,
  setRevyreConfig,
  setPerformanceCards,
  setPendingActivities,
  removeFirstPendingActivity,
} = lmsConfigSlice.actions;

const reducer = { lmsConfig: lmsConfigSlice.reducer };

export default reducer;
